import {
  ChangeDetectionStrategy,
  Component,
  ComponentFactoryResolver,
  ComponentRef,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { FormFieldAnchorDirective } from './form-field-anchor.directive';
import { DataTypeService } from '../data-type/services/data-type.service';
import { DataType, DataTypeComponent } from '../data-type/data-type-component';
import { FieldConfigDTO } from '../../../system/system-dtos';
import { ActivatedRoute, Router } from '@angular/router';
import { createRefUrl } from '../utils/ref-url-utils';

@Component({
  selector: 'app-form-field',
  template: `
    <span class="form-container">
      @if (fieldConfig.editIfTicked) {
        <span class="form-label">
          <mat-label>Edit</mat-label>
          <mat-checkbox [(ngModel)]="tickValue" (ngModelChange)="updateEditMode()"></mat-checkbox>
        </span>
      }
      <span class="ref-container" [ngClass]="{ 'ref-element': isRef() }">
        <ng-template appFormFieldHost></ng-template>
        @if (isRef() && data) {
          <a class="link" [routerLink]="toUrl()"> </a>
        }
      </span>
    </span>
  `,
  changeDetection: ChangeDetectionStrategy.Default,
  styles: [
    `
      .form-container {
        position: static;
        display: flex;
        justify-content: space-between;
      }
      .form-label {
        transform: scale(0.8);
      }
      .ref-container {
        position: relative;
        width: 100%;
        display: flex;
        align-items: center;
      }
      .ref-element {
        cursor: pointer !important;
        transition: 0.5s;
        color: #4a89dc;
        text-decoration: underline;
        width: 100%;
      }
      .link {
        position: absolute;
        left: 0;
        top: 0;
        width: 100%;
        opacity: 0;
        height: 20px;
      }
    `,
  ],
  standalone: false,
})
export class FormFieldComponent implements OnInit, OnDestroy, OnChanges {
  @Input() type: DataType;

  @Input() fieldConfig: FieldConfigDTO;

  @Input() data: any;

  @Input() editMode: boolean = false;

  @Input() formGroup: UntypedFormGroup;

  @Input() rowData: any;

  @Input() hasFilters = false;

  @Output() editModeChange = new EventEmitter<boolean>();

  @Output() formGroupChange = new EventEmitter<UntypedFormGroup>();

  @ViewChild(FormFieldAnchorDirective, { static: true })
  tableCellHost: FormFieldAnchorDirective;

  private instance: DataTypeComponent;

  tickValue: boolean;

  initialEditMode: boolean;

  constructor(
    private componentFactoryResolver: ComponentFactoryResolver,
    private dataTypeService: DataTypeService,
    private router: Router,
    private route: ActivatedRoute
  ) {}

  ngOnInit() {
    this.loadComponent();
  }

  ngOnDestroy() {}

  loadComponent() {
    this.initialEditMode = this.editMode;
    const formField = this.dataTypeService.getFormFieldComponent(this.type);
    const componentFactory = this.componentFactoryResolver.resolveComponentFactory(formField);
    const { viewContainerRef } = this.tableCellHost;

    const componentRef: ComponentRef<DataTypeComponent> = viewContainerRef.createComponent(componentFactory);
    componentRef.location.nativeElement.style.width = '100%';
    this.instance = <DataTypeComponent>componentRef.instance;
    this.instance.data = this.data;
    this.instance.fieldConfig = this.fieldConfig;
    this.instance.editMode = this.editMode;
    this.instance.formGroup = this.formGroup;
    this.instance.hasFilters = this.hasFilters;
    this.instance.setRowData(this.rowData);
    setTimeout(() => this.updateEditMode(), 0);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (this.instance) {
      this.instance.editMode = this.editMode;
    }
  }

  updateEditMode() {
    const tickedToUpdate = !this.fieldConfig.editIfTicked || !!this.tickValue;
    if (tickedToUpdate) {
      this.setValue();
    } else {
      this.formGroup.get(this.fieldConfig.name).setValue(null);
    }
    this.editMode = this.initialEditMode && tickedToUpdate;
    this.instance.editMode = this.editMode;
  }

  private setValue() {
    if (this.fieldConfig.type !== 'checkbox' && this.fieldConfig.type != 'option') {
      return;
    }

    let value = this.data;
    if (!value && this.fieldConfig.type === 'checkbox') {
      value = false;
    }

    if ((value === false || value) && this.formGroup.get(this.fieldConfig.name)) {
      this.formGroup.get(this.fieldConfig.name).setValue(value);
    }
  }

  toUrl(): string {
    return createRefUrl(this.fieldConfig, this.formGroup.getRawValue(), this.route);
  }

  isRef() {
    return !this.editMode && this.fieldConfig.ref && this.formGroup.getRawValue();
  }
}
